/** Generic interstitial overlay — centred card with title, description, and action buttons. */
import { motion } from 'motion/react';
import { Button } from '@/interfaces/session-player/ui';

// ─── Interstitial ─────────────────────────────────────────────────────────────

const EASE = [0.16, 1, 0.3, 1] as const;

export interface InterstitialProps {
  title: string;
  description: string;
  primaryLabel: string;
  primaryDisabled?: boolean;
  onPrimary: () => void;
  secondaryLabel?: string;
  onSecondary?: () => void;
}

export function Interstitial({
  title,
  description,
  primaryLabel,
  primaryDisabled,
  onPrimary,
  secondaryLabel,
  onSecondary,
}: InterstitialProps) {
  return (
    <div
      className="flex min-h-dvh items-center justify-center px-6"
      style={{ background: 'var(--f-bg)', fontFamily: 'var(--f-font-sans)' }}
    >
      <motion.div
        className="w-full max-w-md"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: EASE }}
      >
        <div
          className="rounded-2xl p-8"
          style={{
            background: 'var(--f-bg-raised)',
            border: '1px solid var(--f-border)',
            boxShadow: 'var(--f-shadow-lg)',
          }}
        >
          <h2
            className="font-semibold"
            style={{
              fontSize: 'var(--f-text-lg)',
              color: 'var(--f-fg)',
              fontFamily: 'var(--f-font-display)',
              lineHeight: 'var(--f-leading-tight)',
            }}
          >
            {title}
          </h2>

          <p
            className="mt-3"
            style={{
              fontSize: 'var(--f-text-sm)',
              color: 'var(--f-fg-secondary)',
              lineHeight: 'var(--f-leading-relaxed)',
            }}
          >
            {description}
          </p>

          <div className="mt-8 flex items-center gap-3">
            <Button
              variant="ghost"
              onPress={onPrimary}
              isDisabled={primaryDisabled}
              className="rounded-xl px-5 py-2.5 font-medium transition-all duration-150 disabled:opacity-50"
              style={{
                fontSize: 'var(--f-text-sm)',
                background: 'var(--f-gradient)',
                color: 'var(--f-fg-on-accent)',
                boxShadow: primaryDisabled ? 'none' : 'var(--f-shadow-accent)',
              }}
            >
              {primaryLabel}
            </Button>

            {secondaryLabel && onSecondary && (
              <Button
                variant="ghost"
                onPress={onSecondary}
                className="rounded-xl px-5 py-2.5 font-medium transition-colors duration-150"
                style={{
                  fontSize: 'var(--f-text-sm)',
                  color: 'var(--f-fg-muted)',
                }}
              >
                {secondaryLabel}
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
